/**
 * React Query wrappers for the Codex capacity-alert operator surface (#2575).
 *
 * Query keys
 *   - `['codex-capacity-alerts']`                   — root; every mutation invalidates it
 *   - `['codex-capacity-alerts', 'settings']`       — the global switch and knobs
 *   - `['codex-capacity-alerts', 'state']`          — the live latch
 *   - `['codex-capacity-alerts', 'preview']`        — who the NEXT pass would mail
 *   - `['codex-capacity-alerts', 'incidents']`      — opened / closed events
 *   - `['codex-capacity-alerts', 'history', id]`    — what was already mailed
 *   - `['codex-capacity-alerts', 'suppressions']`   — the operator mutes
 *   - `['codex-capacity-alerts', 'excluded-skus']`  — SKUs cut from both surfaces
 *
 * The preview is a QUERY. It carries no operator-local state — it is a
 * read of the live population — so a refetch after a mute lands is the
 * CORRECT behaviour rather than a hazard.
 */
import type {
  CodexCapacityAlertHistoryEntry,
  CodexCapacityAlertIncident,
  CodexCapacityAlertMailKind,
  CodexCapacityAlertPreviewResponse,
  CodexCapacityAlertSettings,
  CodexCapacityAlertStateResponse,
  CodexCapacityAlertSuppression,
  CodexCapacityAlertTestSendResponse,
  CodexCapacityExcludedSku,
  CreateCodexCapacityAlertSuppressionResponse,
  CreateCodexCapacityExcludedSkuResponse,
  DeleteCodexCapacityAlertSuppressionResponse,
  DeleteCodexCapacityExcludedSkuResponse,
  UpdateCodexCapacityAlertSettingsRequest,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  createCodexCapacityAlertSuppression,
  createCodexCapacityExcludedSku,
  deleteCodexCapacityAlertSuppression,
  deleteCodexCapacityExcludedSku,
  getCodexCapacityAlertPreview,
  getCodexCapacityAlertSettings,
  getCodexCapacityAlertState,
  listCodexCapacityAlertHistory,
  listCodexCapacityAlertIncidents,
  listCodexCapacityAlertSuppressions,
  listCodexCapacityExcludedSkus,
  sendCodexCapacityAlertTest,
  updateCodexCapacityAlertSettings,
} from './codexCapacityAlerts.ts';

export const CODEX_CAPACITY_ALERTS_KEY = ['codex-capacity-alerts'] as const;

function invalidateAll(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: CODEX_CAPACITY_ALERTS_KEY });
}

export function useCodexCapacityAlertSettings(): UseQueryResult<CodexCapacityAlertSettings> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CODEX_CAPACITY_ALERTS_KEY, 'settings'],
    queryFn: async ({ signal }) =>
      (await getCodexCapacityAlertSettings(fetchImpl, signal)).settings,
  });
}

export function useUpdateCodexCapacityAlertSettings(): UseMutationResult<
  CodexCapacityAlertSettings,
  Error,
  UpdateCodexCapacityAlertSettingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateCodexCapacityAlertSettingsRequest) =>
      (await updateCodexCapacityAlertSettings(body, fetchImpl)).settings,
    onSuccess: (settings) => {
      qc.setQueryData([...CODEX_CAPACITY_ALERTS_KEY, 'settings'], settings);
      invalidateAll(qc);
    },
  });
}

export function useCodexCapacityAlertState(): UseQueryResult<CodexCapacityAlertStateResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CODEX_CAPACITY_ALERTS_KEY, 'state'],
    queryFn: ({ signal }) => getCodexCapacityAlertState(fetchImpl, signal),
  });
}

export function useCodexCapacityAlertPreview(
  active: boolean,
): UseQueryResult<CodexCapacityAlertPreviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CODEX_CAPACITY_ALERTS_KEY, 'preview'],
    queryFn: ({ signal }) => getCodexCapacityAlertPreview(fetchImpl, signal),
    enabled: active,
  });
}

export function useCodexCapacityAlertIncidents(): UseQueryResult<
  readonly CodexCapacityAlertIncident[]
> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CODEX_CAPACITY_ALERTS_KEY, 'incidents'],
    queryFn: async ({ signal }) =>
      (await listCodexCapacityAlertIncidents(fetchImpl, signal)).incidents,
  });
}

export function useCodexCapacityAlertHistory(
  incidentId: string | null,
): UseQueryResult<readonly CodexCapacityAlertHistoryEntry[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CODEX_CAPACITY_ALERTS_KEY, 'history', incidentId ?? 'all'],
    queryFn: async ({ signal }) =>
      (await listCodexCapacityAlertHistory(fetchImpl, incidentId, signal)).entries,
  });
}

export function useCodexCapacityAlertSuppressions(): UseQueryResult<
  readonly CodexCapacityAlertSuppression[]
> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CODEX_CAPACITY_ALERTS_KEY, 'suppressions'],
    queryFn: async ({ signal }) =>
      (await listCodexCapacityAlertSuppressions(fetchImpl, signal)).suppressions,
  });
}

export function useCreateCodexCapacityAlertSuppression(): UseMutationResult<
  CreateCodexCapacityAlertSuppressionResponse,
  Error,
  { readonly accountId: string; readonly reason: string | null }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, reason }) =>
      createCodexCapacityAlertSuppression({ account_id: accountId, reason }, fetchImpl),
    onSuccess: () => {
      invalidateAll(qc);
    },
  });
}

export function useDeleteCodexCapacityAlertSuppression(): UseMutationResult<
  DeleteCodexCapacityAlertSuppressionResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => deleteCodexCapacityAlertSuppression(accountId, fetchImpl),
    onSuccess: () => {
      invalidateAll(qc);
    },
  });
}

export function useCodexCapacityExcludedSkus(): UseQueryResult<
  readonly CodexCapacityExcludedSku[]
> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CODEX_CAPACITY_ALERTS_KEY, 'excluded-skus'],
    queryFn: async ({ signal }) =>
      (await listCodexCapacityExcludedSkus(fetchImpl, signal)).excluded_skus,
  });
}

export function useCreateCodexCapacityExcludedSku(): UseMutationResult<
  CreateCodexCapacityExcludedSkuResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skuName: string) =>
      createCodexCapacityExcludedSku({ sku_name: skuName }, fetchImpl),
    onSuccess: () => {
      invalidateAll(qc);
    },
  });
}

export function useDeleteCodexCapacityExcludedSku(): UseMutationResult<
  DeleteCodexCapacityExcludedSkuResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skuName: string) =>
      deleteCodexCapacityExcludedSku({ sku_name: skuName }, fetchImpl),
    onSuccess: () => {
      invalidateAll(qc);
    },
  });
}

export function useCodexCapacityAlertTestSend(): UseMutationResult<
  CodexCapacityAlertTestSendResponse,
  Error,
  { readonly accountIds: readonly string[]; readonly kind: CodexCapacityAlertMailKind }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountIds, kind }) =>
      sendCodexCapacityAlertTest({ account_ids: accountIds, kind }, fetchImpl),
    onSuccess: () => {
      invalidateAll(qc);
    },
  });
}
